/**
 * NewForm design-system EmptyState.
 *
 * Promoted from `/prototypes/empty`, which argued it: of 265 places the app
 * says some version of "nothing here", 20 put an action within reach and 245
 * are a sentence and a full stop. An empty list is the one screen where the
 * reader has nothing to work with, and it was the screen we gave the least
 * help on.
 *
 * "No ads found" also covers three situations whose correct answers are
 * opposites, which is why `tone` exists and why the copy is per-call:
 *
 * - **nothing yet** (`brand`) — offer the thing that creates one.
 * - **no results** (`neutral`) — a filter excluded everything; offer to clear it.
 * - **unavailable** (`warn`) — say why, and offer to retry.
 *
 * `action` IS REQUIRED, and that is the point of the component rather than an
 * oversight in the types. An empty state with no way forward is a dead end,
 * and a required prop is the only version of that rule which survives the
 * next person in a hurry.
 *
 *   <EmptyState
 *     icon={<Plus className="size-5" />}
 *     title="No batches yet"
 *     body="A batch groups the creative you want to test against one another."
 *     action={<Button variant="brand">Launch a batch</Button>}
 *   />
 *
 * Reference: `packages/nf-design-system/preview/components-card.html` for the
 * surface it usually sits on, and `/prototypes/empty` for the argument.
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Body, Title } from "./text";

/** The three situations, as the fill and ink that belong to each. */
const TONE = {
  brand: "bg-nf-ds-tone-brand-bg text-nf-ds-tone-brand-fg",
  neutral: "bg-nf-ds-tone-neutral-bg text-nf-ds-tone-neutral-fg",
  warn: "bg-nf-ds-tone-warn-bg text-nf-ds-tone-warn-fg",
} as const;

export function EmptyState({
  icon,
  tone = "brand",
  title,
  body,
  action,
  secondary,
  className,
}: {
  icon: ReactNode;
  tone?: keyof typeof TONE;
  title: string;
  body: string;
  /** Required. An empty state with no way forward is a dead end. */
  action: ReactNode;
  secondary?: ReactNode;
  /** Placement only. */
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col items-center gap-nf-ds-item px-nf-ds-card py-nf-ds-section text-center",
        className,
      )}
    >
      {/* The icon sits in a tinted disc rather than floating. A bare outline
          glyph on a large empty pane reads as a loading artefact; the disc
          gives it a deliberate size and a ground, so it reads as placed. */}
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex size-10 items-center justify-center rounded-full",
          TONE[tone],
        )}
      >
        {icon}
      </span>

      {/* 46ch, because this is the one screen whose whole job is a sentence
          and a full measure of it is easier to read than a wide one. */}
      <div className="flex max-w-[46ch] flex-col gap-nf-ds-tight">
        <Title>{title}</Title>
        <Body muted>{body}</Body>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-nf-ds-tight pt-nf-ds-tight">
        {action}
        {secondary}
      </div>
    </div>
  );
}
